import { DETAIL_GENERAL_INFO_MAP, DETAIL_RECIPIENT_INFO_MAP, DETAIL_PAYMENT_INFO_MAP } from '../consts';
import useFilters from '../hooks/useFilters';
import { Dialog, Transition } from '@headlessui/react';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { getUserInfo } from 'api/requests/core/users';
import { getOrderStatus, getOrders, updateOrder } from 'api/requests/giftCard/giftCard';
import { GiftCardOrder } from 'api/types/giftCard/giftCard';
import { CrossIcon } from 'assets/svg';
import { Fragment, useState, useCallback, useMemo } from 'react';
import DropdownV2 from 'shared/components/DropdownV2';
import Spinner from 'shared/components/Spinner';
import Spinner2 from 'shared/components/Spinner2';
import { Role } from 'shared/consts/role';
import { queryKeyBuilder } from 'shared/query';

type Props = {
  orderId: string | undefined;
  onModalClose: () => void;
};

function OrderDetails({ orderId, onModalClose }: Props) {
  const queryClient = useQueryClient();
  const urlQueryParams = useFilters();

  const { data: userData } = useQuery({
    queryKey: queryKeyBuilder.currentUser(),
    queryFn: getUserInfo,
  });

  const {
    data: cardStatuses,
    isPending: areStatusesPending,
    isError: areStatusesErrored,
  } = useQuery({
    queryKey: ['giftCardsStatuses'],
    queryFn: getOrderStatus,
    select: (response) => response.data,
  });

  const {
    data: ordersData,
    isPending: areOrdersPending,
    isError: areOrdersErrored,
  } = useQuery({
    queryKey: queryKeyBuilder.giftCardsToShow(urlQueryParams),
    queryFn: () => getOrders(urlQueryParams),
    select: (response) => response.orders,
  });

  const displayedOrder = ordersData?.find((order) => order.id === orderId);
  const [orderStatusOption, setOrderStatusOption] = useState(displayedOrder?.orderStatus ?? '');

  const closeCallback = useCallback(() => {
    setOrderStatusOption('');
    onModalClose();
  }, [setOrderStatusOption, onModalClose]);

  const orderStatusMutation = useMutation({
    mutationFn: () => updateOrder(orderId!, orderStatusOption),
    onMutate: async () => {
      const cacheKey = queryKeyBuilder.giftCardsToShow(urlQueryParams);
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const prevState = queryClient.getQueryData(cacheKey);
      queryClient.setQueryData(cacheKey, (prev: { orders: GiftCardOrder[] }) => ({
        ...prev,
        orders: prev.orders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                orderStatus: orderStatusOption,
              }
            : order
        ),
      }));
      return { prevState };
    },
    onError: (err, resp, context) => {
      queryClient.setQueryData(queryKeyBuilder.giftCardsToShow(urlQueryParams), context?.prevState);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyBuilder.giftCardsToShow(urlQueryParams) });
      closeCallback();
    },
  });

  const canEdit = useMemo(() => {
    const userRoles = userData?.roles ?? [];
    return userRoles.includes(Role.GC_Edit);
  }, [userData]);

  const isLoading = areStatusesPending || areOrdersPending;
  const isError = areStatusesErrored || areOrdersErrored;
  const orderStatusObject = cardStatuses?.find((status) => status.orderStatus === displayedOrder?.orderStatus);

  return (
    <Transition appear show={!!orderId} as={Fragment}>
      <Dialog as="div" className="relative z-30 w-full h-full" onClose={closeCallback}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-palette-secondary transition-opacity opacity-20" />
        </Transition.Child>
        <div className="fixed inset-0">
          <div className="flex h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="max-w-full max-h-full w-full h-full py-8 px-6 gap-8 md:w-[40rem] md:h-[789px] md:py-14 md:px-10 md:gap-14 md:rounded-xl text-palette-secondary overflow-auto scrollbar flex flex-col bg-white shadow-md">
                {isLoading ? (
                  <div className="w-full grow flex flex-col items-center justify-center">
                    <Spinner2 />
                  </div>
                ) : isError ? (
                  <div className="w-full grow flex flex-col items-center justify-center">
                    <div className="mt-6 max-w-[360px] text-center font-klarheit text-3xl">Something went wrong</div>
                    <div className="max-w-[360px] text-center text-base mt-4">Try again later</div>
                    <button
                      className="w-52 justify-center text-center group transition-colors hover:text-palette-gold text-white bg-palette-burgundy flex px-6 py-3 gap-3 items-center rounded-full mt-14"
                      onClick={closeCallback}
                    >
                      <span className="tracking-tight text-base text-center">Close</span>
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2 items-center">
                      <span className="font-klarheit tracking-tight text-3xl">Order Details</span>
                      <div className="grow" />
                      <button className="flex" onClick={closeCallback}>
                        <CrossIcon className="w-[18px] h-[18px]" />
                      </button>
                    </div>
                    <div className="flex flex-col gap-6">
                      <h4 className="font-klarheit tracking-tight text-xl text-palette-secondary"> General info</h4>
                      <div className="flex flex-col bg-palette-sbiu-grey px-4 py-4 gap-6 md:px-10 md:py-8 md:gap-10">
                        {DETAIL_GENERAL_INFO_MAP.map((cellSchema) =>
                          !cellSchema.test || cellSchema.test(displayedOrder) ? (
                            <div key={`${displayedOrder?.id}_${cellSchema.key}`} className="flex justify-between gap-4">
                              <span className="uppercase font-klarheit tracking-tight text-base font-bold w-1/2">
                                {' '}
                                {cellSchema.headerLabel as keyof GiftCardOrder}
                              </span>
                              <span className="text-base w-1/2">
                                {displayedOrder?.[cellSchema.key as keyof GiftCardOrder] ?? '--'}
                              </span>
                            </div>
                          ) : null
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-6">
                      <h4 className="font-klarheit tracking-tight text-xl text-palette-secondary"> Recipient info</h4>
                      <div className="flex flex-col bg-palette-sbiu-grey px-4 py-4 gap-6 md:px-10 md:py-8 md:gap-10">
                        {DETAIL_RECIPIENT_INFO_MAP.map((cellSchema) => (
                          <div key={`${displayedOrder?.id}_${cellSchema.key}`} className="flex justify-between gap-4">
                            <span className="title uppercase font-klarheit tracking-tight text-base font-bold w-1/2">
                              {' '}
                              {cellSchema.headerLabel as keyof GiftCardOrder}
                            </span>
                            <span className="value text-base w-1/2">
                              {displayedOrder?.[cellSchema.key as keyof GiftCardOrder] ?? '--'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-6">
                      <h4 className="font-klarheit tracking-tight text-xl text-palette-secondary">
                        {' '}
                        Payment & Fee info
                      </h4>
                      <div className="flex flex-col bg-palette-sbiu-grey px-4 py-4 gap-6 md:px-10 md:py-8 md:gap-10">
                        {DETAIL_PAYMENT_INFO_MAP.map((cellSchema) => (
                          <div key={`${displayedOrder?.id}_${cellSchema.key}`} className="flex justify-between gap-4">
                            <span className="title uppercase font-klarheit tracking-tight text-base font-bold w-1/2">
                              {' '}
                              {cellSchema.headerLabel as keyof GiftCardOrder}
                            </span>
                            <span className="value text-base w-1/2">
                              {displayedOrder?.[cellSchema.key as keyof GiftCardOrder] ?? '--'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-8">
                      <DropdownV2
                        disabled={!canEdit}
                        options={
                          orderStatusObject?.updatableStatus?.map((status) => ({
                            label: status,
                            value: status,
                          })) ?? []
                        }
                        value={orderStatusOption}
                        onChange={(value: string) => setOrderStatusOption(value)}
                        placeholder="Select..."
                        label="Order status update"
                      />
                    </div>
                    {orderStatusObject?.updatable && (
                      <div className={`${canEdit ? 'visible' : 'invisible'} flex justify-center`}>
                        <button
                          className="group transition-colors hover:text-palette-gold disabled:hover:text-white text-white bg-palette-burgundy flex px-28 py-3 items-center rounded-full disabled:text-palette-secondary-grey disabled:bg-palette-color-bg-grey"
                          disabled={orderStatusMutation.isPending}
                          type="submit"
                          onClick={() => orderStatusMutation.mutate()}
                        >
                          {orderStatusMutation.isPending ? (
                            <Spinner />
                          ) : (
                            <span className="tracking-tight text-base uppercase">save updates</span>
                          )}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default OrderDetails;
